// 넘김이 들고 온 것을 화면이 읽는 **한 자리.**
//
// proposal-presentation.js 와 같은 규율에 선다: **서버가 준 것만 읽고, 화면은 파생하지 않는다.**
// 승격 델타 때 배운 것이 그것이다 — 화면이 서버와 다른 계산을 시작하면 두 정의가 조용히
// 갈리고, 갈린 쪽은 언제나 화면이다.
//
// 그리고 하나 더, 이 파일에만 있는 규율:
//
//   **관측을 사실처럼 말하지 않는다.**
//
// 준비 상태 보고는 "받은 쪽 자리에서 그렇게 보이더라"이지 "이 Loom 이 접근을 허가했다"가
// 아니다. 그래서 아래 라벨은 전부 "…라고 보고됨"으로 끝나고, "준비됨"·"접근 가능" 같은
// 단정하는 말이 없다. 서버도 `readinessSummary` 에 `ready` 를 넣지 않는다 — 같은 이유다.

/** 보고된 상태 → 사람이 읽을 말. 단정하지 않는다. */
const READINESS_LABEL = {
  readable: '지금 읽힌다고 보고됨',
  access_required: '원본 쪽 접근이 필요하다고 보고됨',
  local_binding_required: '로컬 결속이 필요하다고 보고됨',
  revision_mismatch: '약속된 판과 다르다고 보고됨',
  unknown: '확인하지 못했다고 보고됨',
};

/** 판단 축의 색을 쓰지 않는다 — 이건 판단이 아니라 관측이다. */
const READINESS_TONE = {
  readable: 'neutral',
  access_required: 'danger',
  local_binding_required: 'neutral',
  revision_mismatch: 'danger',
  unknown: 'neutral',
};

export function readinessLabel(status) {
  return READINESS_LABEL[status] ?? '알 수 없는 보고';
}

export function readinessTone(status) {
  return READINESS_TONE[status] ?? 'neutral';
}

/**
 * 꾸러미 항목마다 **마지막 보고 하나**를 붙인다.
 *
 * 서버는 보고를 덮어쓰지 않고 쌓는다(어제 못 읽던 것이 오늘 읽히는 것은 두 개의 사실이다).
 * 화면은 그중 마지막만 보여 주되, 그게 마지막일 뿐임을 잊지 않도록 `reportCount` 를 함께 둔다.
 *
 * @returns null 이면 이 넘김에는 꾸러미가 없다 — **빈 꾸러미로 꾸미지 않는다.**
 *          옛 넘김이 그 모양이고, "고르지 않았다"와 "골랐는데 비었다"는 다른 사실이다.
 */
export function readWorkPackage(handoff) {
  const workPackage = handoff?.workPackage ?? null;
  if (!workPackage) return null;

  const reports = Array.isArray(handoff.readiness) ? handoff.readiness : [];
  const latest = new Map();
  const counts = new Map();
  for (const report of reports) {
    latest.set(report.referenceId, report);
    counts.set(report.referenceId, (counts.get(report.referenceId) ?? 0) + 1);
  }

  return {
    baseVersionNo: workPackage.baseVersionNo ?? null,
    expectedResult: workPackage.expectedResult ?? null,
    capturedAt: workPackage.capturedAt ?? null,
    items: (workPackage.items ?? []).map((item) => ({
      ...item,
      report: latest.get(item.referenceId) ?? null,
      reportCount: counts.get(item.referenceId) ?? 0,
    })),
    // 서버가 센 것을 그대로 옮긴다. 화면이 다시 세면 세는 규칙이 갈린다.
    summary: handoff.readinessSummary ?? null,
  };
}

/**
 * 이 항목이 약속한 판을 한 줄로.
 *
 * `pinned` 가 false 면 **고정된 판이 없다**는 뜻이고, 그걸 감추면 받는 쪽은 자기가 본 것이
 * 보낸 쪽이 본 것과 같다고 믿게 된다. 그래서 그 경우를 조용히 넘기지 않고 말한다.
 */
export function targetLabel(item) {
  if (!item.pinned) return '판을 고정하지 않은 참조 — 지금의 그것이지 그때의 그것이 아닙니다';
  return item.targetRevision === null
    ? '이 시점으로 고정된 참조'
    : `이 시점으로 고정 · 판 ${item.targetRevision}`;
}

/** 결과가 없으면 없다고 적는다. 지어내지 않는다. */
export function readResult(handoff) {
  const result = handoff?.result ?? null;
  if (!result) return null;
  return {
    summary: result.summary,
    referenceIds: result.referenceIds ?? [],
    remainingIssues: result.remainingIssues ?? [],
    proposalId: result.proposalId ?? null,
    recordedByNodeId: result.recordedByNodeId ?? null,
  };
}

/** 사람이 적은 여러 줄을 목록으로. 빈 줄은 버린다. */
export function textLines(value) {
  return String(value ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * 자료 서술자 한 줄. Resource Cloud 가 준 것만 읽는다.
 *
 * git 자료는 주소 하나로 다시 찾을 수 없다 — 어느 판의 어느 파일의 어느 줄인지가 함께 있어야
 * 같은 것을 가리킨다. 그래서 그 셋을 화면에도 함께 적는다. commit 은 앞 12자만 보인다:
 * 전체 값은 여전히 서술자에 있고, 줄이는 것은 표시일 뿐이다.
 */
export function sourceLabel(source) {
  if (!source || source.type !== 'git') return null;
  const range = source.range ? ` · ${source.range.startLine}–${source.range.endLine}줄` : '';
  return `git ${String(source.commit ?? '').slice(0, 12)} · ${source.path}${range}`;
}

/**
 * 자료 참조가 **어느 판을 가리키는가.**
 *
 * `snapshot` 이면 locator 의 `version` 이 곧 그 판이고, 목록에 실린 *현재* 판이 아니다.
 * 초기 화면은 「v1 고정」이라고 적어 놓고 링크는 현재 판의 주소를 열었다 — 라벨과 링크가
 * 서로 다른 것을 말하면 둘 다 믿을 수 없게 된다.
 */
export function pinnedAssetVersion(item) {
  if (item?.kind !== 'asset') return null;
  return item.mode === 'snapshot' ? (item.locator?.version ?? null) : null;
}

/**
 * 판을 말하는 두 이름은 **같은 자리에 놓지 않는다.**
 *
 * `registeredVersion` 은 Loom 이 등록한 판(`1`·`2`…), `revision`·git `commit` 은 원본이
 * 자기 식으로 부르는 이름이다. 하나로 합치면 `1` 과 40자 SHA 를 나란히 놓고 다르다고 말하게 된다.
 */
export function manifestVersionLabel(manifest) {
  if (!manifest) return null;
  const registered = manifest.registeredVersion ?? manifest.currentVersion ?? null;
  return registered === null ? null : `Loom 등록 판 v${registered}`;
}

export function originRevisionLabel(manifest) {
  if (!manifest) return null;
  if (manifest.source?.type === 'git') {
    return `원본 commit ${String(manifest.source.commit ?? '').slice(0, 12)}`;
  }
  return manifest.revision ? `원본 표기 ${manifest.revision}` : null;
}

/**
 * 이 대화 참조를 **이 화면에서** 열 수 있나.
 *
 * Context Pack 이 참조마다 실어 준 provider 능력만 본다. 외부 provider 의 대화를 native
 * 경로(`/t/:threadId`)로 링크하면, 누르는 순간 없는 Thread 를 찾다가 실패한다 — 열리지 않는
 * 문을 그려 두느니 왜 못 여는지 적는 편이 낫다.
 */
export function nativeThreadLink(reference) {
  const threadId = reference?.locator?.threadId;
  if (!threadId) return null;
  const readable = reference.providerAvailable === true
    && reference.capabilities?.read === true
    && reference.transport === 'native';
  return readable ? threadId : null;
}

/**
 * 반환 결과에 담을 수 있는 참조.
 *
 * **꾸러미에 들어 있던 것만이 아니다.** 일하면서 새로 붙인 근거가 결과의 핵심인데, 초기
 * 화면은 입력 목록만 보여 줘서 그걸 담을 방법이 없었다. 꾸러미 항목을 앞에 두되(보통 그
 * 자리부터 고른다), Task 의 모든 참조를 고를 수 있어야 한다.
 */
export function resultReferenceChoices({ references = [], workPackage = null }) {
  const packaged = new Set((workPackage?.items ?? []).map((item) => item.referenceId));
  const ordered = [
    ...references.filter((reference) => packaged.has(reference.referenceId)),
    ...references.filter((reference) => !packaged.has(reference.referenceId)),
  ];
  return ordered.map((reference) => ({ ...reference, inPackage: packaged.has(reference.referenceId) }));
}

/**
 * 결과로 가리킬 수 있는 제안들.
 *
 * 초기 화면은 사람에게 `prop_…` 를 손으로 적으라고 했다. 그건 화면이 이미 알고 있는 것을
 * 사람에게 외우게 하는 일이다. 열린 제안·밀려난 제안·현재 이해를 만든 제안 셋을 모은다.
 */
export function proposalChoices(pack) {
  const seen = new Set();
  const choices = [];
  const add = (proposal, state) => {
    if (!proposal?.proposalId || seen.has(proposal.proposalId)) return;
    seen.add(proposal.proposalId);
    choices.push({ proposalId: proposal.proposalId, number: proposal.number ?? null, state });
  };
  for (const proposal of pack?.openProposals ?? []) add(proposal, 'open');
  for (const proposal of pack?.supersededProposals ?? []) add(proposal, 'superseded');
  add(pack?.currentVersion?.promotedFromProposal, 'promoted');
  return choices;
}

export function proposalLabel(choice) {
  const state = { open: '열림', superseded: '대체됨', promoted: '승격됨' }[choice.state] ?? choice.state;
  return choice.number ? `제안 p${choice.number} · ${state}` : `${choice.proposalId} · ${state}`;
}
